import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CreditCard, PaymentForm } from 'react-square-web-payments-sdk';

const CheckoutPage = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [shippingInfo, setShippingInfo] = useState({ rate: 0, tier: '' });
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    address: '',
    city: '',
    state: 'MA',
    zip: '',
  });

  const subtotal = getCartTotal();
  const totalQuantity = cart.reduce((sum, item) => sum + item.quantity, 0);
  const tax = subtotal * 0.0625;
  const total = subtotal + shippingInfo.rate + tax;

  // Calculate shipping when cart changes
  useEffect(() => {
    const calculateShipping = async () => {
      if (totalQuantity > 0) {
        try {
          const result = await api.calculateShipping(totalQuantity);
          setShippingInfo({ rate: result.shipping_rate, tier: result.tier });
        } catch (error) {
          console.error('Error calculating shipping:', error);
          // Fallback shipping rate
          setShippingInfo({ rate: 9.95, tier: 'Standard' });
        }
      }
    };
    calculateShipping();
  }, [totalQuantity]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePayment = async (token) => {
    setLoading(true);
    setPaymentError('');

    try {
      // Create order first
      const orderData = {
        customerEmail: formData.customerEmail,
        customerName: formData.customerName,
        customerPhone: formData.customerPhone,
        items: cart.map(item => ({
          sku: item.sku,
          name: item.name,
          price: item.retailPrice,
          quantity: item.quantity,
        })),
        subtotal,
        tax,
        shipping: shippingInfo.rate,
        total,
        shippingAddress: `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}`,
      };

      const order = await api.createOrder(orderData);

      // Process payment with Square
      const paymentData = {
        sourceId: token.token,
        amount: Math.round(total * 100), // Convert to cents
        currency: 'USD',
        orderId: order.id,
        buyerEmail: formData.customerEmail,
      };

      const paymentResult = await api.createPayment(paymentData);

      if (paymentResult.success) {
        // Clear cart and navigate to success page
        clearCart();
        navigate(`/order-confirmation/${order.id}`);
      } else {
        setPaymentError('Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setPaymentError(error.response?.data?.detail || 'There was an error processing your payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentError = (errors) => {
    console.error('Square payment errors:', errors);
    setPaymentError('Unable to process payment. Please check your card details and try again.');
  };

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  // Check if all required fields are filled
  const isFormValid = formData.customerName && 
                      formData.customerEmail && 
                      formData.address && 
                      formData.city && 
                      formData.state && 
                      formData.zip;

  return (
    <div className="paper-texture min-h-screen py-12">
      <div className="max-w-5xl mx-auto px-6">
        <h1 className="font-display text-5xl mb-12" data-testid="checkout-title">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Checkout Form */}
          <div className="lg:col-span-2 space-y-8">
            {/* Contact Information */}
            <div className="bg-white border border-alt-bg p-8">
              <h2 className="font-display text-2xl mb-6">Contact Information</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="customerName" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    Full Name *
                  </Label>
                  <Input
                    id="customerName"
                    name="customerName"
                    type="text"
                    required
                    value={formData.customerName}
                    onChange={handleInputChange}
                    className="w-full"
                    data-testid="customer-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="customerEmail" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    Email *
                  </Label>
                  <Input
                    id="customerEmail"
                    name="customerEmail"
                    type="email"
                    required
                    value={formData.customerEmail}
                    onChange={handleInputChange}
                    className="w-full"
                    data-testid="customer-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="customerPhone" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    Phone (optional)
                  </Label>
                  <Input
                    id="customerPhone"
                    name="customerPhone"
                    type="tel"
                    value={formData.customerPhone}
                    onChange={handleInputChange}
                    className="w-full"
                    placeholder="508-555-1234"
                  />
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white border border-alt-bg p-8">
              <h2 className="font-display text-2xl mb-6">Shipping Address</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="address" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    Street Address *
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    required
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full"
                    data-testid="address-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city" className="text-sm font-semibold uppercase tracking-wider mb-2">
                      City *
                    </Label>
                    <Input
                      id="city"
                      name="city"
                      type="text"
                      required
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full"
                      data-testid="city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state" className="text-sm font-semibold uppercase tracking-wider mb-2">
                      State *
                    </Label>
                    <Input
                      id="state"
                      name="state"
                      type="text"
                      required
                      value={formData.state}
                      onChange={handleInputChange}
                      className="w-full"
                      data-testid="state-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="zip" className="text-sm font-semibold uppercase tracking-wider mb-2">
                    ZIP Code *
                  </Label>
                  <Input
                    id="zip"
                    name="zip"
                    type="text"
                    required
                    pattern="[0-9]{5}"
                    value={formData.zip}
                    onChange={handleInputChange}
                    className="w-full"
                    data-testid="zip-input"
                  />
                </div>
              </div>
            </div>

            {/* Square Payment Form */}
            <div className="bg-white border border-alt-bg p-8">
              <h2 className="font-display text-2xl mb-6">Payment Information</h2>
              
              {!isFormValid && (
                <div className="mb-6 p-4 bg-alt-bg text-[#2D2A26]">
                  <p className="text-sm">Please fill in all shipping information above before entering payment details.</p>
                </div>
              )}

              {paymentError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700" data-testid="payment-error">
                  <p className="text-sm">{paymentError}</p>
                </div>
              )}

              {isFormValid && (
                <PaymentForm
                  applicationId={process.env.REACT_APP_SQUARE_APP_ID}
                  locationId={process.env.REACT_APP_SQUARE_LOCATION_ID}
                  cardTokenizeResponseReceived={handlePayment}
                  createPaymentRequest={() => ({
                    countryCode: 'US',
                    currencyCode: 'USD',
                    total: {
                      amount: total.toFixed(2),
                      label: 'Total',
                    },
                  })}
                >
                  <CreditCard
                    buttonProps={{
                      css: {
                        backgroundColor: '#CC0000',
                        fontSize: '14px',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: '#A30000',
                        },
                      },
                    }}
                  />
                </PaymentForm>
              )}

              <div className="mt-4 text-xs text-[#8C8885] text-center">
                <p>Secure payment powered by Square</p>
                <p className="mt-1">Your payment information is encrypted and secure</p>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="bg-white border border-alt-bg p-8 soft-shadow">
              <h2 className="font-display text-2xl mb-6">Order Summary</h2>
              
              <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                {cart.map((item) => (
                  <div key={item.sku} className="flex justify-between text-sm">
                    <span className="text-[#2D2A26]">
                      {item.name} × {item.quantity}
                    </span>
                    <span className="font-semibold">
                      ${(item.retailPrice * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t border-alt-bg pt-4 space-y-3 mb-6">
                <div className="flex justify-between text-[#2D2A26]">
                  <span>Subtotal ({totalQuantity} {totalQuantity === 1 ? 'jar' : 'jars'})</span>
                  <span data-testid="checkout-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#2D2A26]">
                  <span>Shipping</span>
                  <span data-testid="checkout-shipping">
                    ${shippingInfo.rate.toFixed(2)}
                  </span>
                </div>
                {shippingInfo.tier && (
                  <p className="text-xs text-[#8C8885]">
                    Shipping tier: {shippingInfo.tier}
                  </p>
                )}
                <div className="flex justify-between text-[#2D2A26]">
                  <span>Tax (MA 6.25%)</span>
                  <span data-testid="checkout-tax">${tax.toFixed(2)}</span>
                </div>
                <div className="border-t border-alt-bg pt-3 flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="price-tag text-2xl" data-testid="checkout-total">
                    ${total.toFixed(2)}
                  </span>
                </div>
              </div>

              {!isFormValid && (
                <div className="text-center text-sm text-[#8C8885] p-4 bg-alt-bg">
                  Fill in shipping information to continue
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
